import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { certifications } from '../data/portfolio';

export default function Certifications() {
  const [ref, inView] = useInView(0.1);
  const [expanded, setExpanded] = useState(null);

  return (
    <section id="certifications" className="section">
      <div style={{ position: 'absolute', top: 0, left: '5%', right: '5%', height: 1, background: 'linear-gradient(to right, transparent, rgba(0,245,255,0.08), transparent)' }} />
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '3.5rem' }}
        >
          <div className="sec-tag">05 · Certifications</div>
          <h2 className="sec-title">
            Credentials{' '}
            <span style={{ background: 'linear-gradient(90deg, var(--cyan), var(--teal))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Earned
            </span>
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '1.5rem' }}>
          {certifications.map((cert, i) => (
            <motion.div
              key={cert.title}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.75, delay: i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setExpanded(expanded === i ? null : i)}
              style={{
                background: 'rgba(0,245,255,0.03)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: `1px solid ${expanded === i ? cert.color + '45' : 'rgba(0,245,255,0.1)'}`,
                borderRadius: '18px',
                padding: 'clamp(1.5rem, 3vw, 2rem)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                transition: 'border-color 0.3s, box-shadow 0.3s, transform 0.3s',
                transform: expanded === i ? 'translateY(-3px)' : 'none',
                boxShadow: expanded === i ? `0 20px 60px ${cert.color}15, 0 0 30px ${cert.color}10` : 'none',
              }}
            >
              {/* Glow line top */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: expanded === i ? 2 : 1,
                background: `linear-gradient(to right, transparent, ${cert.color}, transparent)`,
                boxShadow: expanded === i ? `0 0 20px ${cert.color}` : 'none',
                transition: 'all 0.35s',
              }} />

              {/* BG orb */}
              <div style={{
                position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%',
                background: cert.color, opacity: expanded === i ? 0.06 : 0.025, filter: 'blur(40px)',
                transition: 'opacity 0.35s', pointerEvents: 'none',
              }} />

              {/* Icon + toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', position: 'relative' }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '12px',
                  background: cert.color + '15',
                  border: `1px solid ${cert.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: cert.color,
                  boxShadow: expanded === i ? `0 0 20px ${cert.color}30` : 'none',
                  transition: 'box-shadow 0.35s',
                }}>
                  {cert.icon === 'Brain' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.44-4.14z"/>
                      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.44-4.14z"/>
                    </svg>
                    ) : cert.icon === 'Database' ? (
                     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                       <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                       <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                       <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
                     </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  )}
                </div>
                <motion.div
                  animate={{ rotate: expanded === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ color: 'var(--grey2)', marginTop: '0.25rem' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                </motion.div>
              </div>

              <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1.15rem', color: 'var(--white)', marginBottom: '0.4rem', position: 'relative' }}>
                {cert.title}
              </h3>

              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.65rem', letterSpacing: '0.08em', color: cert.color, textShadow: `0 0 10px ${cert.color}60` }}>
                  {cert.issuer}
                </span>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: '0.58rem', letterSpacing: '0.05em',
                  color: 'var(--grey2)', padding: '0.12rem 0.5rem',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '4px',
                }}>
                  {cert.period}
                </span>
              </div>

              {/* Expandable */}
              <motion.div
                initial={false}
                animate={{ height: expanded === i ? 'auto' : 0, opacity: expanded === i ? 1 : 0, marginTop: expanded === i ? '1.25rem' : 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{ overflow: 'hidden', position: 'relative' }}
              >
                <div style={{ height: 1, background: `linear-gradient(to right, ${cert.color}40, transparent)`, marginBottom: '1rem' }} />
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                  {cert.details.map((d, j) => (
                    <li key={j} style={{ display: 'flex', gap: '0.6rem', fontSize: '0.83rem', color: 'var(--grey)', lineHeight: 1.6, alignItems: 'flex-start' }}>
                      <span style={{ color: cert.color, flexShrink: 0, marginTop: '0.3rem', fontSize: '0.5rem', textShadow: `0 0 6px ${cert.color}` }}>◆</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <div style={{ marginTop: '0.75rem', fontFamily: 'var(--font-display)', fontSize: '0.55rem', color: 'var(--grey2)', display: 'flex', alignItems: 'center', gap: '0.35rem', position: 'relative' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                Diploma · {expanded === i ? 'Click to collapse' : 'Click for details'}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
